// FAR-88 §1 A1–A6 / FAR-90 §2.1 A1–A6: per-tab approval of a Production
// Plan. Each Planning Board tab has its own approval entry, stored on the plan
// under its own key; the two are independent. Pure, so which entry a tab
// reads, the wording and the stamp are tested without a browser.
import { formatDate } from '../../utils/format.js'

export const APPROVED = 'Approved' // must match models.PlanStatusApproved

// Board tab key (boardShell TABS) -> approval key on the plan (models.ApprovalTab*).
export const APPROVAL_KEYS = { '3month': 'months_0_3', '4-12month': 'months_4_18' }

// The tab names used in messages when the month range isn't known yet.
export const APPROVAL_TAB_NAMES = { months_0_3: 'Months 0–3', months_4_18: 'Months 4–18' }

export function approvalKey(tab) {
  return APPROVAL_KEYS[tab] || null
}

// FAR-88 Q2 / FAR-90 O1: only Approver and Planner+Approver. Roles come as
// the comma-separated custom:roles claim, so Planner+Approver is
// "planner,approver". Must match handlers.approvalRoles.
const APPROVAL_ROLES = ['approver', 'planner+approver']

export function canApprove(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : String(user?.roles || '').split(',')
  return roles.map((r) => String(r).trim().toLowerCase()).some((r) => APPROVAL_ROLES.includes(r))
}

export const NOT_ALLOWED_TOOLTIP = 'Only users with the Approver or Planner+Approver role can approve'

// tabApproval is the approval entry the given board tab reads, or null.
//
// Backward compatibility: a plan approved under the old single plan-level
// approval (status Approved, approvedAt/approvedBy on the plan, no per-tab
// entries) reads as BOTH tabs approved with that stamp. The server does the
// same (models.PlanVersion.Normalize); this covers a response from a server
// without it. Once a plan has per-tab entries, only those count.
export function tabApproval(plan, tab) {
  const key = approvalKey(tab)
  if (!plan || !key) return null
  const approvals = plan.approvals
  if (approvals && Object.keys(approvals).length > 0) return approvals[key] || null
  if (plan.status === APPROVED) {
    return {
      userId: plan.approvedBy || '',
      userName: plan.approvedByName || '',
      approvedAt: plan.approvedAt || '',
      basePlanId: plan.basePlanId || '',
      legacy: true,
    }
  }
  return null
}

function who(entry) {
  return entry?.userName || entry?.userId || 'unknown user'
}

// "Nov 4, 2026, 3:30 PM" in the user's locale and time zone.
export function formatDateTime(iso, locale) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(locale, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  })
}

// A3: the badge reads "{user name} · {date}"; the full ID and time on hover.
export function badgeText(entry) {
  if (!entry) return null
  return entry.approvedAt ? `${who(entry)} · ${formatDate(entry.approvedAt)}` : who(entry)
}

export function badgeTooltip(entry, locale) {
  if (!entry) return ''
  const when = formatDateTime(entry.approvedAt, locale)
  const parts = [`Approved by ${who(entry)}`]
  if (entry.userId) parts.push(`User ID: ${entry.userId}`)
  if (when) parts.push(`At: ${when}`)
  if (entry.legacy) parts.push('Approved for the whole plan before approval was per tab')
  return parts.join('\n')
}

// FAR-88 Q5: the approval stays after a Base Plan or Modification change, and
// the tab shows "changed since approval". The entry records the Base Plan and
// Modification list it was approved against; individual modification edits
// are not versioned, so only a switch of either is detected.
export function changedSinceApproval(entry, plan) {
  if (!entry || !plan) return false
  if (entry.basePlanId && (plan.basePlanId || '') !== entry.basePlanId) return true
  if (entry.modificationListId && (plan.modificationListId || '') !== entry.modificationListId) return true
  return false
}

// The two modals (FAR-88 A2 / A5, FAR-90 A2). ctx: { planName, tabLabel,
// basePlanLabel, modificationName }.
export function confirmText(action, ctx = {}, entry = null) {
  const plan = ctx.planName || 'this plan'
  const subject = ctx.tabLabel ? `${plan} · ${ctx.tabLabel}` : plan
  if (action === 'approve') {
    const details = [ctx.basePlanLabel, ctx.modificationName ? `Modification: ${ctx.modificationName}` : null].filter(Boolean)
    return {
      title: 'Approve plan',
      body: `Approve ${subject} as of now? Your name and today's date will be recorded on this tab's plan.`,
      details,
      confirm: 'Approve',
    }
  }
  const date = entry?.approvedAt ? formatDate(entry.approvedAt) : 'an unknown date'
  return {
    title: 'Remove approval',
    body: `Remove the approval of ${subject} by ${who(entry)} on ${date}? The approval record will be deleted.`,
    details: [],
    confirm: 'Remove Approval',
  }
}

// A6: approving an approved tab, or removing an approval that is gone,
// returns 409. The board refreshes the plan and says why.
export function conflictMessage(action, tabName) {
  const tab = tabName || 'This tab'
  return action === 'approve'
    ? `${tab} was already approved by someone else. The plan has been refreshed.`
    : `${tab} no longer has an approval to remove. The plan has been refreshed.`
}

// The POST body for /plans/{id}/approve and /unapprove. months: the tab's
// months ({ iso: '2026-10' }), recorded as the horizon at approval.
export function approvalRequestBody(action, plantCode, tab, months = []) {
  const body = { plant: plantCode, tab: approvalKey(tab) }
  if (action === 'approve' && months.length > 0) {
    body.firstMonth = months[0].iso
    body.lastMonth = months[months.length - 1].iso
  }
  return body
}
